(() => {
  const ID_PATTERN = /^[A-Za-z0-9]{8}$/;

  function rootUrl() {
    return window.FoxTheme?.routes?.root_url || window.Shopify?.routes?.root || '/';
  }

  function cartUrl() {
    return window.FoxTheme?.routes?.cart_url || `${rootUrl()}cart`;
  }

  function publicImageUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || url.username || url.password) return '';
      if (/^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|\[)/.test(url.hostname) || !url.hostname.includes('.')) return '';
      return url.href;
    } catch {
      return '';
    }
  }

  function referrerHost() {
    try {
      return document.referrer ? new URL(document.referrer).hostname : '';
    } catch {
      return '';
    }
  }

  function track(name, params) {
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false || window.lampsPreviewBuild) return;
    window.LampsRoomEvents?.track(name, params);
    if (window.GA4CustomEvents?.pushCustomEvent) {
      window.GA4CustomEvents.pushCustomEvent(name, params);
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  }

  async function fetchJson(url, { timeout = 8000, ...options } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      return { ok: response.ok, status: response.status, body: await response.json().catch(() => null) };
    } catch {
      return { ok: false, status: 0, body: null };
    } finally {
      clearTimeout(timer);
    }
  }

  function setMeta(selector, attr, value) {
    if (!value) return;
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      const match = selector.match(/\[(property|name)="([^"]+)"\]/);
      if (!match) return;
      el.setAttribute(match[1], match[2]);
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  }

  class RoomSharePage {
    constructor(root) {
      this.root = root;
      this.workerUrl = (root.dataset.workerUrl || '').replace(/\/$/, '');
      this.pageTitleSuffix = root.dataset.pageTitleSuffix || '';
      this.loading = root.querySelector('[data-share-loading]');
      this.missing = root.querySelector('[data-share-missing]');
      this.content = root.querySelector('[data-share-content]');
      this.image = root.querySelector('[data-share-image]');
      this.title = root.querySelector('[data-share-title]');
      this.variant = root.querySelector('[data-share-variant]');
      this.price = root.querySelector('[data-share-price]');
      this.addToCartBtn = root.querySelector('[data-share-add-to-cart]');
      this.productLink = root.querySelector('[data-share-product-link]');
      this.createOwnLink = root.querySelector('[data-share-create-own]');
      this.realRoomsStrip = root.querySelector('real-rooms-strip');
      this.record = null;
      this.product = null;
      this.matchedVariant = null;
    }

    async init() {
      const id = new URLSearchParams(location.search).get('r') || '';
      this.shareId = id;

      if (!ID_PATTERN.test(id) || !this.workerUrl) {
        this.showMissing();
        return;
      }

      const shareResponse = await fetchJson(`${this.workerUrl}/share/${encodeURIComponent(id)}`);
      if (!shareResponse.ok || !shareResponse.body?.id) {
        this.showMissing();
        return;
      }
      this.record = shareResponse.body;

      const imageUrl = publicImageUrl(this.record.image_url);
      const handle = typeof this.record.product_handle === 'string' ? this.record.product_handle : '';
      if (!imageUrl || !handle) {
        this.showMissing();
        return;
      }

      const productResponse = await fetchJson(`${rootUrl()}products/${handle}.js`);
      if (!productResponse.ok || !productResponse.body?.id) {
        this.showMissing();
        return;
      }
      this.product = productResponse.body;

      const variantId = Number(this.record.variant_id);
      this.matchedVariant = this.product.variants?.find((entry) => entry.id === variantId) || null;
      if (!this.matchedVariant) {
        this.showMissing();
        return;
      }

      this.render(imageUrl);
      track('room_share_opened', {
        share_id: id,
        source: this.record.source || '',
        referrer: referrerHost(),
      });
    }

    showMissing() {
      this.loading?.setAttribute('hidden', '');
      this.content?.setAttribute('hidden', '');
      this.missing?.removeAttribute('hidden');
    }

    render(imageUrl) {
      const product = this.product;
      const variant = this.matchedVariant;

      if (this.image) this.image.src = imageUrl;
      if (this.title) this.title.textContent = product.title || '';
      if (this.variant) this.variant.textContent = variant.title === 'Default Title' ? '' : variant.title || '';
      if (this.price) {
        const format = window.FoxTheme?.settings?.moneyFormat || '${{amount}}';
        this.price.textContent = window.FoxTheme?.Currency?.formatMoney
          ? window.FoxTheme.Currency.formatMoney(variant.price, format)
          : `$${(variant.price / 100).toFixed(2)}`;
      }

      const productUrl = product.url || `${rootUrl()}products/${product.handle}`;
      if (this.productLink) {
        this.productLink.href = productUrl;
        this.productLink.addEventListener('click', () => track('room_share_product_clicked', { share_id: this.shareId }));
      }
      if (this.createOwnLink) {
        const createOwnUrl = new URL(productUrl, location.origin);
        createOwnUrl.searchParams.set('variant', String(variant.id));
        createOwnUrl.searchParams.set('view_in_room', '1');
        this.createOwnLink.href = createOwnUrl.href;
        this.createOwnLink.addEventListener('click', () => track('room_share_create_own_clicked', { share_id: this.shareId }));
      }

      this.setupAddToCart(variant);
      this.realRoomsStrip?.setAttribute('data-handle', product.handle);

      this.loading?.setAttribute('hidden', '');
      this.missing?.setAttribute('hidden', '');
      this.content?.removeAttribute('hidden');

      this.setMetaTags(product, imageUrl);
    }

    setMetaTags(product, imageUrl) {
      try {
        if (product.title) document.title = `${[product.title, this.pageTitleSuffix].filter(Boolean).join(' — ')} | lamps.com`;
        setMeta('meta[property="og:title"]', 'content', product.title);
        setMeta('meta[property="og:image"]', 'content', imageUrl);
        setMeta('meta[name="twitter:image"]', 'content', imageUrl);
      } catch {
        /* best effort */
      }
    }

    setupAddToCart(variant) {
      const button = this.addToCartBtn;
      if (!button) return;

      if (variant.available === false) {
        button.disabled = true;
        button.textContent = button.dataset.labelSoldOut || '';
        return;
      }

      button.disabled = false;
      button.textContent = button.dataset.labelDefault || '';
      button.addEventListener('click', () => this.addToCart(variant, button));
    }

    async addToCart(variant, button) {
      if (button.disabled) return;
      button.disabled = true;
      button.textContent = button.dataset.labelPending || '';

      try {
        if (typeof window.LampsRoomCommerce?.addToCart !== 'function') throw new Error('commerce_unavailable');
        await window.LampsRoomCommerce.addToCart({ variantId: variant.id, handle: this.product.handle });

        await fetchJson(`${rootUrl()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              room_share_id: this.shareId,
              room_share_source: this.record.source || '',
            },
          }),
        });

        track('room_share_added_to_cart', {
          share_id: this.shareId,
          source: this.record.source || '',
          variant_id: String(variant.id),
          product_id: String(this.product.id),
        });

        button.textContent = button.dataset.labelAdded || '';

        if (document.querySelector('cart-drawer')) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          button.disabled = false;
          button.textContent = button.dataset.labelDefault || '';
        } else {
          location.href = cartUrl();
        }
      } catch {
        button.textContent = button.dataset.labelError || '';
        button.disabled = false;
      }
    }
  }

  function boot() {
    const root = document.querySelector('[data-room-share]');
    if (!root) return;
    new RoomSharePage(root).init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
